/**
 * Загрузка документов в перечень приложений (решение от 25.09.2026).
 *
 * Любой распознанный документ показывает, какой пункт перечня он закрывает,
 * и по кнопке отмечает его собранным. Справки, которые полей дела
 * не заполняют, здесь же проверяются по содержанию:
 *
 *   • о несудимости — сведения об осуждении и об уголовном преследовании,
 *     а также та ли это справка: ФИО и дата рождения против должника;
 *   • Гостехнадзор и ГИМС — нет ли техники и судов, не было ли регистрационных
 *     действий за три года;
 *   • ФНС о сальдо ЕНС — отрицательное сальдо предлагается строкой
 *     «Обязательных платежей».
 *
 * Отметка «собран» ставится только по кнопке: документ с замечаниями
 * (судимость есть, справка на другого человека) собранным не считается,
 * пока юрист не решит.
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { newRowId, type ObligatoryPayment } from '@bd/domain';
import { money } from '@bd/doc-engine/money';
import { api, type AppendixImport } from '../../../api.js';
import { DocumentUpload } from './DocumentUpload.js';
import type { ImportHandle, Preloaded } from './import-handle.js';

const norm = (value: string) => value.toUpperCase().replace(/Ё/g, 'Е').replace(/\s+/g, ' ').trim();

export const AppendixDocumentsImport = forwardRef<ImportHandle, {
  caseId: string;
  debtor: { fullName: string; birthDate: string | null; inn: string };
  /** Пункты текущего перечня: ключ, номер, текст */
  appendix: Array<{ key: string; num: number; text: string }>;
  checklist: Record<string, boolean>;
  readOnly: boolean;
  onMark: (keys: string[]) => void;
  onAddObligatory: (payment: ObligatoryPayment) => void;
  /** Встроенный режим (import-handle.ts): документы уже разобраны, кнопки — общие */
  preloaded?: Preloaded<AppendixImport>;
}>(function AppendixDocumentsImport({ caseId, debtor, appendix, checklist, readOnly, onMark, onAddObligatory, preloaded }, ref) {
  const embedded = Boolean(preloaded);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [results, setResults] = useState<AppendixImport[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (files: File[]) => {
    setBusy(true);
    setErrors([]);
    setResults([]);
    setMessage(null);
    const loaded: AppendixImport[] = [];
    const failed: string[] = [];
    for (const file of files) {
      try {
        loaded.push(await api.importAppendixDocument(caseId, file));
      } catch (e) {
        failed.push(`${file.name}: ${(e as Error).message}`);
      }
    }
    setResults(loaded);
    setErrors(failed);
    setBusy(false);
  };

  /** Замечания по документу: проверки содержания и сверка с должником. */
  const problems = (r: AppendixImport): string[] => {
    const list = r.checks.filter((c) => !c.ok).map((c) => c.text);
    if (r.subject?.fullName && debtor.fullName && norm(r.subject.fullName) !== norm(debtor.fullName)) {
      list.push(`Документ выдан на «${r.subject.fullName}», а должник — «${debtor.fullName}»`);
    }
    if (r.subject?.birthDate && debtor.birthDate && r.subject.birthDate !== debtor.birthDate) list.push('Дата рождения в документе не совпадает с должником');
    if (r.subject?.inn && debtor.inn && r.subject.inn !== debtor.inn.replace(/\s/g, '')) list.push('ИНН в документе не совпадает с должником');
    return list;
  };

  const line = (key: string | null) => (key ? appendix.find((a) => a.key === key) : undefined);
  // Отмечаются только документы без замечаний и с пунктом в текущем перечне
  const markable = [...new Set(results.filter((r) => r.appendixKey && line(r.appendixKey) && problems(r).length === 0).map((r) => r.appendixKey!))].filter(
    (key) => !checklist[key],
  );

  const mark = () => {
    onMark(markable);
    const summary = `Отмечено собранными: ${markable.length}.`;
    setMessage(summary);
    return summary;
  };

  // Встроенный режим: документы приходят разобранными, отметка — общей кнопкой
  useEffect(() => {
    if (!preloaded) return;
    setResults(preloaded.results);
    setErrors(preloaded.errors);
  }, [preloaded]);
  useImperativeHandle(ref, () => ({ accept: () => (markable.length ? mark() : null) }));

  return (
    <div className="report-import">
      {!embedded && (
        <DocumentUpload
          label="Загрузить документы клиента"
          busyLabel="Читаю документы…"
          hint="Отметит собранные приложения. Можно всю папку клиента сразу. Файлы нигде не сохраняются."
          busy={busy}
          disabled={readOnly}
          onFiles={(files) => void load(files)}
        />
      )}

      {errors.map((error, i) => (
        <p key={i} className="error-text">
          {error}
        </p>
      ))}
      {message && <p className="hint">{message}</p>}

      {results.length > 0 && (
        <div className="draft">
          <table className="simple-table">
            <thead>
              <tr>
                <th>Документ</th>
                <th>Пункт перечня</th>
                <th>Проверка</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => {
                const item = line(r.appendixKey);
                const issues = problems(r);
                return (
                  <tr key={i}>
                    <td>
                      {r.title}
                      {r.place && <div className="hint">Сведения из него загружаются в {r.place}</div>}
                    </td>
                    <td>
                      {!r.appendixKey ? (
                        <span className="hint">в перечне нет</span>
                      ) : item ? (
                        <>
                          {item.num}. {item.text}
                          {checklist[item.key] && <div className="saved">уже отмечен</div>}
                        </>
                      ) : (
                        <span className="hint">в перечне этого дела пункта нет</span>
                      )}
                    </td>
                    <td>
                      {r.checks
                        .filter((c) => c.ok)
                        .map((c) => (
                          <div key={c.text} className="saved">
                            {c.text}
                          </div>
                        ))}
                      {issues.map((text) => (
                        <div key={text} className="warning">
                          {text}
                        </div>
                      ))}
                      {r.taxDebt && (
                        <button
                          type="button"
                          className="link-button"
                          disabled={readOnly}
                          onClick={() => {
                            onAddObligatory({
                              id: newRowId(),
                              name: 'Задолженность по единому налоговому счёту',
                              arrears: r.taxDebt!.amount,
                              isBusiness: false,
                              confirmingDocument: r.taxDebt!.confirmingDocument,
                            });
                            setMessage(`В «Обязательные платежи» добавлено ${money(r.taxDebt!.amount).digitsWithKopecks} ₽. Разбейте по налогам, если нужно.`);
                          }}
                        >
                          Добавить в «Обязательные платежи»
                        </button>
                      )}
                      {r.checks.length === 0 && issues.length === 0 && <span className="hint">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {results.some((r) => problems(r).length > 0) && (
            <p className="warning">Документы с замечаниями собранными не отмечаются — решите по ним и отметьте вручную.</p>
          )}
          {!embedded && (
            <div className="toolbar">
              <button type="button" className="primary" disabled={readOnly || markable.length === 0} onClick={mark}>
                Отметить собранными ({markable.length})
              </button>
              <button type="button" className="link-button" onClick={() => setResults([])}>
                Закрыть
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
